import { z } from 'zod';

export const ContactKind = z.enum([
  'supervisor',
  'signatory',
  'admin_contact',
  'speaker',
  'committee_external',
  'partner',
  'outreach_target',
  'collaborator',
  'knowledge_user',
  'lived_experience',
  'event_attendee',
  'competition',
  'department_head',
  'training_director',
  'advertising_network',
  'ambassador',
  'other',
]);
export type ContactKind = z.infer<typeof ContactKind>;

export const ContactIdParams = z.strictObject({ id: z.guid() });
export type ContactIdParams = z.input<typeof ContactIdParams>;

/** page and limit are clamped by the service (limit 1–200). */
export const ContactListQuery = z.strictObject({
  q: z.string().optional(),
  kind: z.union([ContactKind, z.literal('')]).optional(),
  tag: z.string().optional(),
  archived: z.enum(['active', 'archived', 'all']).optional(),
  page: z.coerce.number().int().optional(),
  limit: z.coerce.number().int().optional(),
});
export type ContactListQuery = z.input<typeof ContactListQuery>;

const text = z.string().nullable().optional();

/**
 * The service stores blank text as null, cuts long text, requires a non-blank name and checks the
 * email address. Tags (lowercase labels) come as a list or comma-separated text; the service
 * checks their count and length after cleaning.
 */
export const CreateContactRequest = z.strictObject({
  name: z.string(),
  email: text,
  organization: text,
  title: text,
  notes: text,
  kinds: z.array(ContactKind).nullable().optional(),
  tags: z
    .union([z.array(z.string()), z.string()])
    .nullable()
    .optional(),
});
export type CreateContactRequest = z.input<typeof CreateContactRequest>;

export const UpdateContactRequest = CreateContactRequest.partial();
export type UpdateContactRequest = z.input<typeof UpdateContactRequest>;

export const ContactMember = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
});
export type ContactMember = z.infer<typeof ContactMember>;

/** A contact as the API returns it (dates as ISO strings). Restricted contacts come back redacted. */
export const Contact = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  organization: z.string().nullable(),
  title: z.string().nullable(),
  kinds: z.array(z.string()),
  tags: z.array(z.string()),
  notes: z.string().nullable(),
  source: z.string(),
  userId: z.string().nullable(),
  createdBy: z.string().nullable(),
  archivedAt: z.string().nullable(),
  archivedBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  member: ContactMember.nullable(),
  restricted: z.literal(true).optional(),
});
export type Contact = z.infer<typeof Contact>;

export const ContactPage = z.object({
  contacts: z.array(Contact),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  kinds: z.array(z.string()),
  tags: z.array(z.string()),
});
export type ContactPage = z.infer<typeof ContactPage>;

/** A refused write; a duplicate email (409) carries the contact that already has it. */
export const ContactErrorResponse = z.object({ error: z.string(), contact: Contact.optional() });
export type ContactErrorResponse = z.infer<typeof ContactErrorResponse>;
